import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Field, FieldRenderProps, FieldWrapper, Form, FormElement, FormFieldSet, FormRenderProps } from '@progress/kendo-react-form';
import { Loader } from '@progress/kendo-react-indicators';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Error, Hint, Label } from '@progress/kendo-react-labels';
import React, { useState } from 'react';
import { FeedbackType } from '../../../common/enums';
import { FeedbackTypeOptions } from '../../../components/weather/settings/settings-options';
import { CheckmarkCircleOutline, FeedbackOutline } from '../../../icons/icons';
import { UserService } from '../../../services/axios/axios-api-v2-generated';
import feVersion from '../../../utils/version-helper';
import { useEvent } from '../../contexts/event-bus';
import { DropdownListWithValueField } from '../inputs/DropdownListWithValueField';

export function SendFeedbackWindow() {
    const [visible, setVisible] = useState(false);
    useEvent('open-feedback', () => {
        setVisible(true);
    });

    if (!visible) {
        return null;
    }

    return <FeedbackForm onClose={() => setVisible(false)} />;
}
const EMAIL_REGEX = /\S+@\S+\.\S+/;

const ValidatedInput = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, ...others } = fieldRenderProps;
    return (
        <div>
            <Input {...others} />
            {touched && validationMessage && <Error>{validationMessage}</Error>}
        </div>
    );
};

const FormTextArea = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, touched, label, id, valid, disabled, hint, type, optional, ...others } = fieldRenderProps;

    const showValidationMessage = touched && validationMessage;
    const showHint = !showValidationMessage && hint;
    const hindId = showHint ? `${id}_hint` : '';
    const errorId = showValidationMessage ? `${id}_error` : '';

    return (
        <FieldWrapper>
            <Label editorId={id} editorValid={valid} editorDisabled={disabled} optional={optional}>
                {label}
            </Label>
            <div className={'k-form-field-wrap'}>
                <TextArea valid={valid} id={id} disabled={disabled} rows={4} ariaDescribedBy={`${hindId} ${errorId}`} {...others} />
                {showHint && <Hint id={hindId}>{hint}</Hint>}
                {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
            </div>
        </FieldWrapper>
    );
};

const ValidatedDropdown = (fieldRenderProps: FieldRenderProps) => {
    const { validationMessage, label, visited } = fieldRenderProps;
    return (
        <div>
            <Label>{label}</Label>
            <DropdownListWithValueField data={fieldRenderProps.data} value={fieldRenderProps.value} onChange={fieldRenderProps.onChange} />
            {visited && validationMessage && <Error>{validationMessage}</Error>}
        </div>
    );
};

type ValidationErrors = { [key: string]: string };

function FeedbackForm(props: { onClose?: () => void }) {
    const [sent, setSent] = useState(false);
    const [sentError, setSentError] = React.useState(false);
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [formErrors, setFormErrors] = React.useState<ValidationErrors>({});

    const formValidator = React.useCallback(
        (values: any) => {
            const errors: ValidationErrors = {};

            const { name, email, feedback } = values;

            if (name.length === 0) {
                errors.name = 'Name is required';
            }

            if (!EMAIL_REGEX.test(email)) {
                errors.email = 'Invalid email format';
            }

            // TODO: phone number validation?

            if (feedback.length === 0) {
                errors.feedback = 'Feedback is required';
            }
            return Object.keys(errors).length > 0 ? errors : undefined;
        },
        [formErrors]
    );

    const handleChange = React.useCallback(
        (fieldName: string) => {
            if (formErrors[fieldName]) {
                setFormErrors((prev) => {
                    const { [fieldName]: _removed, ...rest } = prev;
                    return rest;
                });
            }
        },
        [formErrors]
    );

    const handleSubmit = React.useCallback(async (values: { [name: string]: any }) => {
        setIsSubmitting(true);
        setSentError(false);

        try {
            const response = await UserService.createFeedbackAsync(
                {
                    body: {
                        name: values.name,
                        phoneNumber: values.phoneNumber,
                        email: values.email,
                        feedbackType: values.type,
                        feedback: values.feedback,
                        version: feVersion.version
                    }
                },
                {
                    withAuthorization: true
                }
            );

            if (response.responseCode) {
                setSentError(true);
            } else {
                setSent(true);
            }
        } catch {
            setSentError(true);
            //
        } finally {
            setIsSubmitting(false);
        }
    }, []);

    const initialFormValues = React.useMemo(
        () => ({
            name: '',
            email: '',
            phoneNumber: '',
            type: FeedbackType.generalComment,
            feedback: ''
        }),
        []
    );

    return (
        <Dialog
            onClose={props.onClose}
            contentStyle={{ maxHeight: '80vh' }}
            width="min(600px, 80vw)"
            title={
                <div className="d-flex align-items-center gap-2">
                    <SvgIcon size="large" icon={FeedbackOutline} />
                    <span> Feedback</span>
                </div>
            }>
            {sent && (
                <div className="d-flex align-items-center gap-1">
                    <SvgIcon icon={CheckmarkCircleOutline} themeColor="success" />
                    Feedback sent.
                </div>
            )}
            {!sent && (
                <div className="d-flex flex-column gap-2 ">
                    <Form
                        initialValues={initialFormValues}
                        onSubmit={handleSubmit}
                        validator={formValidator}
                        errors={formErrors}
                        onChange={handleChange}
                        render={(formRenderProps: FormRenderProps) => (
                            <FormElement>
                                <FormFieldSet>
                                    <div className="mb-2">
                                        <Field
                                            name="name"
                                            component={ValidatedInput}
                                            label="Name*"
                                            placeholder=" "
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <Field
                                            name="email"
                                            type="email"
                                            component={ValidatedInput}
                                            label="Email*"
                                            placeholder=" "
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <Field
                                            name="phoneNumber"
                                            component={ValidatedInput}
                                            placeholder=" "
                                            label="Phone number"
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <Field
                                            name="type"
                                            component={ValidatedDropdown}
                                            data={FeedbackTypeOptions}
                                            label="Type"
                                            disabled={isSubmitting}
                                        />
                                    </div>

                                    <div className="mb-2">
                                        <Field
                                            name="feedback"
                                            placeholder=" "
                                            component={FormTextArea}
                                            label="Feedback*"
                                            disabled={isSubmitting}
                                        />
                                    </div>
                                </FormFieldSet>

                                {sentError && <Error>Could not send feedback. Please try again later.</Error>}
                                <Button
                                    className="w-100 mt-2"
                                    disabled={!formRenderProps.allowSubmit || isSubmitting}
                                    type="submit"
                                    themeColor={'primary'}>
                                    {isSubmitting ? <Loader type="pulsing" /> : 'Send'}
                                </Button>
                            </FormElement>
                        )}
                    />
                </div>
            )}
        </Dialog>
    );
}
